import { useState, useEffect } from 'react';
import { PageTemplateEditor } from '@app/components/PageTemplateEditor';
import { EmailInlineEditor } from '@app/components/EmailDesignEditor';
import { MessageInlineEditor } from '@app/components/MessageDesignEditor';
import { ReviewFormInlineEditor } from '@app/components/ReviewFormDesignEditor';
import { FeedbackFormDesignEditor } from '@app/components/TemplateList/FeedbackFormDesignEditor';
import { PositiveWindowDesignEditor } from '@app/components/TemplateList/PositiveWindowDesignEditor';
import { NegativeWindowDesignEditor } from '@app/components/TemplateList/NegativeWindowDesignEditor';
import { reviewTemplate } from '@app/components/EmailTemplateEditor/templates';
import EditIcon from '@app/assets/icons/edit-gray.svg?react';
import ArrowLeft from '@app/assets/icons/arrow-left-light-gray.svg?react';
import InfoIcon from '@app/assets/icons/info.svg?react';
import { Tooltip } from '@app/components/Tooltip/Tooltip';
import clsx from 'clsx';
import { getEmailPreviewHtml, getMessagePreviewHtml, getReviewPreviewHtml, getFeedbackPreviewHtml, getPositivePreviewHtml, getNegativePreviewHtml } from '@app/utils/htmlTemplates';
import { getBusinessInfo } from '@app/services/business';
import { DEFAULT_EMAIL_DESIGN_CONTENT, DEFAULT_MESSAGE_DESIGN_CONTENT } from '@app/utils/consts';
import { EmailDesignContent, MessageDesignContent } from '@app/models/messageTemplates';

interface Template {
  id: string;
  name: string;
  type: 'email' | 'message' | 'review' | 'feedback' | 'positive' | 'negative';
  description: string;
  template: string;
}

interface TemplateListProps {
  open: boolean;
  onClose: () => void;
  onSelectTemplate: (template: Template) => void;
  templateMode?: 'email' | 'text';
  storageProperty?: string;
  // Open directly in the inline editor for this template type, skipping the template list
  initialEditingType?: Template['type'];
  // Starting content when storage has no saved design yet (e.g. the selected business template). Only "Save" writes it to storage
  initialEmailContent?: EmailDesignContent;
  initialMessageContent?: MessageDesignContent;
}

const mockTemplates: Template[] = [
  {
    id: '1',
    name: 'Default email template',
    type: 'email',
    description: 'Default email template for review requests',
    template: reviewTemplate,
  },
  {
    id: '2',
    name: 'Review form template',
    type: 'review',
    description: 'Template for the review form',
    template: reviewTemplate,
  },
  {
    id: '3',
    name: 'Feedback form template',
    type: 'feedback',
    description: 'Template for the feedback form',
    template: reviewTemplate,
  },
  {
    id: '4',
    name: 'Positive review template',
    type: 'positive',
    description: 'Template shown after positive review',
    template: reviewTemplate,
  },
  {
    id: '5',
    name: 'Negative review template',
    type: 'negative',
    description: 'Template shown after negative review',
    template: reviewTemplate,
  },
];

const mockTextTemplates: Template[] = [
  {
    id: '1',
    name: 'Default message template',
    type: 'message',
    description: 'Default message template for review requests',
    template: 'Hi, we hope you enjoyed our service! Your feedback matters to us. Please take a moment to share your thoughts and help us improve. Click <review-link>. Thank you! - {{business-name}}',
  },
  {
    id: '2',
    name: 'Review form template',
    type: 'review',
    description: 'Template for the review form',
    template: reviewTemplate,
  },
  {
    id: '3',
    name: 'Feedback form template',
    type: 'feedback',
    description: 'Template for the feedback form',
    template: reviewTemplate,
  },
  {
    id: '4',
    name: 'Positive review template',
    type: 'positive',
    description: 'Template shown after positive review',
    template: reviewTemplate,
  },
  {
    id: '5',
    name: 'Negative review template',
    type: 'negative',
    description: 'Template shown after negative review',
    template: reviewTemplate,
  },
];

const emailTemplateTypes = [
  { key: 'email', label: 'Email' },
  { key: 'review', label: 'Review' },
  { key: 'feedback', label: 'Feedback' },
  { key: 'positive', label: 'Positive' },
  { key: 'negative', label: 'Negative' },
];

const textTemplateTypes = [
  { key: 'message', label: 'Message' },
  { key: 'review', label: 'Review' },
  { key: 'feedback', label: 'Feedback' },
  { key: 'positive', label: 'Positive' },
  { key: 'negative', label: 'Negative' },
];

export const TemplateList = ({
  open,
  onClose,
  onSelectTemplate,
  templateMode = 'email',
  storageProperty = 'campaignFormData',
  initialEditingType,
  initialEmailContent,
  initialMessageContent,
}: TemplateListProps) => {
  const templates = templateMode === 'text' ? mockTextTemplates : mockTemplates;
  const templateTypes = templateMode === 'text' ? textTemplateTypes : emailTemplateTypes;
  const initialTemplate = templates.find((t) => t.type === initialEditingType) || templates[0];
  const [selectedTemplate, setSelectedTemplate] = useState<Template>(initialTemplate);
  const [activeTab, setActiveTab] = useState<string>(initialTemplate.type);
  const [isTemplateEditorOpen, setIsTemplateEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [inlineEditingType, setInlineEditingType] = useState<Template['type'] | null>(initialEditingType ?? null);
  const [hasLoadedSavedData, setHasLoadedSavedData] = useState(false);
  const [messageInlineContent, setMessageInlineContent] = useState<string>(initialMessageContent?.message ?? DEFAULT_MESSAGE_DESIGN_CONTENT.message);
  const [messagePreviewName, setMessagePreviewName] = useState<string>(initialMessageContent?.previewName ?? DEFAULT_MESSAGE_DESIGN_CONTENT.previewName);
  const [messageLogo, setMessageLogo] = useState<string>(initialMessageContent?.logoUrl ?? DEFAULT_MESSAGE_DESIGN_CONTENT.logoUrl);

  // Add body class when template editor is open to hide headers via CSS
  useEffect(() => {
    if (open) {
      document.body.classList.add('template-editor-open');
      // Update breadcrumb based on template mode
      const breadcrumbElement = document.getElementById('template-editor-breadcrumb');
      if (breadcrumbElement) {
        const templateTitle = templateMode === 'text' ? 'Default text template' : 'Default email template';
        breadcrumbElement.textContent = templateTitle;
      }
    } else {
      document.body.classList.remove('template-editor-open');
    }
    
    // Cleanup on unmount
    return () => {
      document.body.classList.remove('template-editor-open');
    };
  }, [open, templateMode]);

  // Update breadcrumb when inline editing type changes
  useEffect(() => {
    if (open && inlineEditingType) {
      const breadcrumbElement = document.getElementById('template-editor-breadcrumb');
      if (breadcrumbElement) {
        let templateTitle = '';
        switch (inlineEditingType) {
          case 'email':
            templateTitle = 'Email design';
            break;
          case 'message':
            templateTitle = 'Message design';
            break;
          case 'review':
            templateTitle = 'Review form design';
            break;
          case 'feedback':
            templateTitle = 'Feedback form design';
            break;
          case 'positive':
            templateTitle = 'Positive window design';
            break;
          case 'negative':
            templateTitle = 'Negative window design';
            break;
          default:
            templateTitle = templateMode === 'text' ? 'Default text template' : 'Default email template';
        }
        breadcrumbElement.textContent = templateTitle;
      }

      // Also update localStorage to ensure DashboardLayout can read the current template type
      try {
        const storedData = localStorage.getItem(storageProperty);
        const parsedData = storedData ? JSON.parse(storedData) : {};
        
        // Add a flag to indicate which template type is currently being edited
        const updatedData = {
          ...parsedData,
          currentTemplateType: inlineEditingType
        };
        
        localStorage.setItem(storageProperty, JSON.stringify(updatedData));
      } catch (error) {
        // ignore localStorage errors
      }
    }
  }, [inlineEditingType, open, templateMode]);

  const [emailInlineContent, setEmailInlineContent] = useState<EmailDesignContent>(initialEmailContent ?? DEFAULT_EMAIL_DESIGN_CONTENT);
  const [reviewFormContent, setReviewFormContent] = useState({
    heading: 'Leave a review',
    description: 'How would you rate your experience with {{business-name}}?',
    starsColor: '#FF9500',
    buttonsColor: '#007bff',
    defaultPlatform: 'google'
  });

  // Feedback form content state
  const [feedbackFormContent, setFeedbackFormContent] = useState({
    tags: ['Navigation system', 'Unpleasant design', 'Limited features', 'Technical issues', 'Lack of information', 'Payment problems', 'Inadequate support', 'Other'],
    inputLabel: 'What can we do to improve your experience?',
    referenceContacts: true,
    buttonText: 'Send direct feedback',
    buttonColor: '#52B6FE'
  });

  // Positive window content state
  const [positiveWindowContent, setPositiveWindowContent] = useState({
    heading: 'Thank you for you review',
    description: 'We make it easy for your to share your exact review on our other platforms, just click the buttons below, it helps us out tremendously!',
    imageUrl: '/src/assets/illustrations/good-review.svg',
    additionalPlatforms: {
      google: true,
      yelp: false
    },
    additionalPlatformsTitle: 'Leave the same review on another platforms',
    additionalPlatformsDescription: 'You don\'t need to re-enter anything',
    buttonsColor: '#3B82F6'
  });

  // Negative window content state
  const [negativeWindowContent, setNegativeWindowContent] = useState({
    heading: 'We are very sorry that you had a bad experience!',
    description: 'We will do our best to correct the negative aspects you have identified. Thank you for being transparent with us!',
    imageUrl: '/src/assets/illustrations/sad-cloud.svg'
  });

  // Business logo state
  const [businessLogo, setBusinessLogo] = useState<string>('');

  // Fetch business logo when component mounts
  useEffect(() => {
    const fetchBusinessLogo = async () => {
      try {
        const response = await getBusinessInfo();
        if (response?.data?.photo?.url) {
          setBusinessLogo(response.data.photo.url);
        }
      } catch (error) {
        console.error('Error fetching business logo:', error);
      }
    };
    
    if (open) {
      fetchBusinessLogo();
    }
  }, [open]);


  const handleTemplateSelect = (template: Template) => {
    setSelectedTemplate(template);
    setActiveTab(template.type);
  };

  // Load saved data when inline editing type changes
  useEffect(() => {
    if (inlineEditingType && !hasLoadedSavedData) {
      const storedData = localStorage.getItem(storageProperty);
      if (storedData) {
        try {
          const parsedData = JSON.parse(storedData);
          
          if (inlineEditingType === 'email' && parsedData.emailDesignContent) {
            setEmailInlineContent(parsedData.emailDesignContent);
          }
          
          if (inlineEditingType === 'message' && parsedData.messageDesignContent) {
            setMessageLogo(parsedData.messageDesignContent.logoUrl || '');
            setMessagePreviewName(parsedData.messageDesignContent.previewName || DEFAULT_MESSAGE_DESIGN_CONTENT.previewName);

            // Update old placeholders to new format if they exist
            let message = parsedData.messageDesignContent.message || DEFAULT_MESSAGE_DESIGN_CONTENT.message;
            message = message.replace(/<review-link>/g, '{{buttonUrl}}').replace(/{{business-name}}/g, '{{business-name}}');
            setMessageInlineContent(message);
          }
          
          if (inlineEditingType === 'review' && parsedData.reviewFormDesignContent) {
            setReviewFormContent(parsedData.reviewFormDesignContent);
          }
          
          if (inlineEditingType === 'feedback' && parsedData.feedbackFormDesignContent) {
            setFeedbackFormContent(parsedData.feedbackFormDesignContent);
          }
          
          if (inlineEditingType === 'positive' && parsedData.positiveWindowDesignContent) {
            setPositiveWindowContent(parsedData.positiveWindowDesignContent);
          }
          
          if (inlineEditingType === 'negative' && parsedData.negativeWindowDesignContent) {
            setNegativeWindowContent(parsedData.negativeWindowDesignContent);
          }
          
          setHasLoadedSavedData(true);
        } catch (error) {
          console.error('Error loading saved data:', error);
        }
      }
    }
  }, [inlineEditingType, hasLoadedSavedData]);

  const handleEditTemplate = (template: Template) => {
    // Set the inline editing type
    setInlineEditingType(template.type);
    
    // Update the selected template and active tab to show the correct preview
    setSelectedTemplate(template);
    setActiveTab(template.type);
  };

  const handleTemplateSave = (_template: string, editedHtml: string) => {
    // Update the template in the list
    const updatedTemplate = { ...selectedTemplate, template: editedHtml };
    setSelectedTemplate(updatedTemplate);
    
    // Update the mockTemplates array
    const templateIndex = mockTemplates.findIndex(t => t.id === selectedTemplate.id);
    if (templateIndex !== -1) {
      mockTemplates[templateIndex] = updatedTemplate;
    }
    
    // Call the parent callback
    onSelectTemplate(updatedTemplate);
  };



  const handleSaveAndContinue = () => {
    // Save current inline editor content to localStorage
    const storedData = localStorage.getItem(storageProperty);
    const parsedData = storedData ? JSON.parse(storedData) : {};
    
    let updatedData = { ...parsedData };
    
    // Save email content if email editor is active
    if (inlineEditingType === 'email') {
      const emailHtmlTemplate = `<!DOCTYPE html><html><head><style>body{font-family:Arial,sans-serif;margin:0;padding:20px;background-color:#f4f4f4}.container{background-color:#fff;padding:20px;border-radius:5px;box-shadow:0 2px 5px rgba(0,0,0,.1);max-width:600px;margin:0 auto;text-align:center}.content{text-align:left;margin:16px 0}.logo-container{text-align:center;margin-bottom:20px}.logo-container img{max-width:100%;height:auto;display:block;border:0;margin:0 auto;}.button{display:inline-block;padding:12px 24px;background-color:${emailInlineContent.buttonColor};color:white;text-decoration:none;border-radius:5px;font-weight:bold;margin:20px 0 0 0}.button-container{text-align:center;margin:20px 0}.footer{padding:24px;text-align:center;margin-top:30px}.footer p{font-size:12px;color:#6b7280;margin:0 0 8px 0}.footer a{color:#3b82f6;text-decoration:none;font-size:12px}.footer-logo{font-weight:600;font-size:14px;color:#6b7280;margin-bottom:4px}.footer-powered{font-size:12px;color:#9ca3af}</style></head><body><div class="container"><div class="logo-container">${emailInlineContent.logoUrl ? `<img src="${emailInlineContent.logoUrl}" alt="Business Logo" width="200" height="100" style="object-fit: contain;"/>` : '<div style="text-align: center; font-weight: bold; color: #666;">LOGO</div>'}</div><div class="content"><h1 style="font-size:24px;font-weight:700;color:#111827;margin:0 0 16px 0">${emailInlineContent.heading}</h1><p style="font-size:16px;line-height:1.6;color:#374151;margin:0 0 24px 0">${emailInlineContent.message.replace(/\./g, '.<br>').replace(/\n/g, '</p><p>')}</p></div><div class="button-container"><a href="{{buttonUrl}}" class="button" style="color:#ffffff !important; font-weight:bold;">${emailInlineContent.buttonText}</a></div><div class="footer"><p>You are receiving this email because you opted in via our website.</p><p>Address, #PC City, State.</p><a href="#">Unsubscribe</a><div style="margin-top:16px">${emailInlineContent.logoUrl ? `<img src="${emailInlineContent.logoUrl}" alt="Business Logo" style="max-width:100px;max-height:50px;object-fit:contain;margin-bottom:4px;"/>` : '<div class="footer-logo">LOGO</div>'}<div class="footer-powered">Powered by EchoRatings</div></div></div></div></body></html>`;
      
      updatedData = {
        ...updatedData,
        updatedEmailTemplate: emailHtmlTemplate,
        emailDesignContent: emailInlineContent,
      };
    }
    
    // Save message content if message editor is active
    if (inlineEditingType === 'message') {
      updatedData = {
        ...updatedData,
        updatedMessageTemplate: messageInlineContent,
        messageDesignContent: {
          logoUrl: messageLogo,
          previewName: messagePreviewName,
          message: messageInlineContent
        },
      };
    }
    
    // Save review form content if review editor is active
    if (inlineEditingType === 'review') {
      // Generate HTML template for review form design
      const reviewFormHtmlTemplate = `<!DOCTYPE html>
<html>
<head>
<style>
body {
  font-family: Arial, sans-serif;
  font-size: 16px;
  margin: 0;
  padding: 20px;
  background-color: #f4f4f4;
  color: #000000;
}
.container {
  background-color: #fff;
  padding: 20px;
  border-radius: 5px;
  box-shadow: 0 2px 5px rgba(0,0,0,.1);
}
.review-header {
  text-align: center;
  margin-bottom: 20px;
}
.review-heading {
  font-size: 24px;
  font-weight: bold;
  color: #111827;
  margin-bottom: 10px;
}
.review-description {
  font-size: 16px;
  color: #374151;
  line-height: 1.5;
}
.stars-container {
  text-align: center;
  margin: 20px 0;
}
.star {
  font-size: 32px;
  cursor: pointer;
  margin: 0 5px;
  transition: color 0.2s;
}
.star.selected {
  color: ${reviewFormContent.starsColor};
}
.star.unselected {
  color: #d1d5db;
}
.platform-selector {
  margin: 20px 0;
  padding: 15px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background-color: #f9fafb;
}
.review-input {
  width: 100%;
  min-height: 100px;
  padding: 15px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
}
.submit-button {
  width: 100%;
  padding: 15px;
  background-color: ${reviewFormContent.buttonsColor};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s;
}
.submit-button:hover {
  opacity: 0.9;
}
</style>
</head>
<body>
  <div class="container">
    <div class="review-header">
      <h1 class="review-heading">${reviewFormContent.heading}</h1>
      <p class="review-description">${reviewFormContent.description.replace(/{{business-name}}/g, 'Business')}</p>
    </div>
    
    <div class="stars-container">
      <div class="star selected">★</div>
      <div class="star selected">★</div>
      <div class="star selected">★</div>
      <div class="star unselected">★</div>
      <div class="star unselected">★</div>
    </div>
    
    <div class="platform-selector">
      <p><strong>Default Platform:</strong> ${reviewFormContent.defaultPlatform.charAt(0).toUpperCase() + reviewFormContent.defaultPlatform.slice(1)}</p>
    </div>
    
    <div>
      <label for="review-text">Review:</label>
      <textarea 
        id="review-text" 
        class="review-input" 
        placeholder="Describe your experience..."
      ></textarea>
    </div>
    
    <button class="submit-button">
      Leave Review on ${reviewFormContent.defaultPlatform.charAt(0).toUpperCase() + reviewFormContent.defaultPlatform.slice(1)}
    </button>
  </div>
</body>
</html>`;
      
      updatedData = {
        ...updatedData,
        updatedReviewFormTemplate: reviewFormHtmlTemplate, // Save as HTML template
        reviewFormDesignContent: reviewFormContent, // Save the design content object
      };
    }
    
    // Save feedback form content if feedback editor is active
    if (inlineEditingType === 'feedback') {
      // Generate HTML template for feedback form design
      const feedbackFormHtmlTemplate = `<!DOCTYPE html>
<html>
<head>
<style>
body {
  font-family: Arial, sans-serif;
  font-size: 16px;
  margin: 0;
  padding: 20px;
  background-color: #f4f4f4;
  color: #000000;
}
.container {
  background-color: #fff;
  padding: 20px;
  border-radius: 5px;
  box-shadow: 0 2px 5px rgba(0,0,0,.1);
}
.feedback-header {
  text-align: center;
  margin-bottom: 20px;
}
.feedback-heading {
  font-size: 24px;
  font-weight: bold;
  color: #111827;
  margin-bottom: 10px;
}
.feedback-description {
  font-size: 16px;
  color: #374151;
  line-height: 1.5;
}
.tags-container {
  margin: 20px 0;
}
.tag {
  display: inline-block;
  padding: 8px 16px;
  margin: 4px;
  background-color: #f3f4f6;
  border-radius: 20px;
  font-size: 14px;
  color: #374151;
}
.feedback-input {
  width: 100%;
  min-height: 100px;
  padding: 15px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  margin: 20px 0;
}
.submit-button {
  width: 100%;
  padding: 15px;
  background-color: ${feedbackFormContent.buttonColor};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s;
}
.submit-button:hover {
  opacity: 0.9;
}
</style>
</head>
<body>
  <div class="container">
    <div class="feedback-header">
      <h1 class="feedback-heading">We're sorry about your experience</h1>
      <p class="feedback-description">Please help us improve by providing more details about what went wrong.</p>
    </div>
    
    <div class="tags-container">
      <h3>Common issues:</h3>
      ${feedbackFormContent.tags.map(tag => `<span class="tag">${tag}</span>`).join('')}
    </div>
    
    <div>
      <label for="feedback-text">${feedbackFormContent.inputLabel}</label>
      <textarea 
        id="feedback-text" 
        class="feedback-input" 
        placeholder="Describe what went wrong..."
      ></textarea>
    </div>
    
    <button class="submit-button">
      ${feedbackFormContent.buttonText}
    </button>
  </div>
</body>
</html>`;
      
      updatedData = {
        ...updatedData,
        updatedFeedbackFormTemplate: feedbackFormHtmlTemplate, // Save as HTML template
        feedbackFormDesignContent: feedbackFormContent, // Save the design content object
      };
    }
    
    // Save positive window content if positive editor is active
    if (inlineEditingType === 'positive') {
      // Generate HTML template for positive window design
      const positiveWindowHtmlTemplate = `<!DOCTYPE html>
<html>
<head>
<style>
body {
  font-family: Arial, sans-serif;
  font-size: 16px;
  margin: 0;
  padding: 20px;
  background-color: #f4f4f4;
  color: #000000;
}
.container {
  background-color: #fff;
  padding: 20px;
  border-radius: 5px;
  box-shadow: 0 2px 5px rgba(0,0,0,.1);
}
.positive-header {
  text-align: center;
  margin-bottom: 20px;
}
.positive-heading {
  font-size: 24px;
  font-weight: bold;
  color: #111827;
  margin-bottom: 10px;
}
.positive-description {
  font-size: 16px;
  color: #374151;
  line-height: 1.5;
}
.image-container {
  text-align: center;
  margin: 20px 0;
}
.image-container img {
  max-width: 200px;
  height: auto;
  border-radius: 8px;
}
.additional-platforms {
  margin: 20px 0;
  padding: 20px;
  background-color: #f9fafb;
  border-radius: 8px;
}
.platform-title {
  font-size: 18px;
  font-weight: bold;
  color: #111827;
  margin-bottom: 8px;
}
.platform-description {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 16px;
}
.platform-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}
.platform-button {
  padding: 12px 20px;
  background-color: ${positiveWindowContent.buttonsColor};
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;
}
.platform-button:hover {
  opacity: 0.9;
}
.platform-button.disabled {
  background-color: #d1d5db;
  cursor: not-allowed;
}
</style>
</head>
<body>
  <div class="container">
    <div class="positive-header">
      <h1 class="positive-heading">${positiveWindowContent.heading}</h1>
      <p class="positive-description">${positiveWindowContent.description}</p>
    </div>
    
    <div class="image-container">
      <img src="${positiveWindowContent.imageUrl}" alt="Success" />
    </div>
    
    <div class="additional-platforms">
      <h3 class="platform-title">${positiveWindowContent.additionalPlatformsTitle}</h3>
      <p class="platform-description">${positiveWindowContent.additionalPlatformsDescription}</p>
      <div class="platform-buttons">
        ${positiveWindowContent.additionalPlatforms.google ? 
          '<button class="platform-button">Leave review on Google</button>' : 
          '<button class="platform-button disabled" disabled>Google (unavailable)</button>'
        }
        ${positiveWindowContent.additionalPlatforms.yelp ? 
          '<button class="platform-button">Leave review on Yelp</button>' : 
          '<button class="platform-button disabled" disabled>Yelp (unavailable)</button>'
        }
      </div>
    </div>
  </div>
</body>
</html>`;
      
      // Create content object - preserve user-uploaded images (data URLs)
      const contentToSave = {
        ...positiveWindowContent,
      };
      // Only convert default paths to empty string, preserve all data URLs and other URLs
      if (contentToSave.imageUrl) {
        // If it's a data URL (user uploaded), keep it as-is
        if (!contentToSave.imageUrl.startsWith('data:')) {
          // If it's the default hardcoded path, convert to empty string
          if (contentToSave.imageUrl === '/src/assets/illustrations/good-review.svg') {
            contentToSave.imageUrl = '';
          }
          // Otherwise, keep it as-is (could be imported URL or other custom URL)
        }
      }
      
      updatedData = {
        ...updatedData,
        updatedPositiveWindowTemplate: positiveWindowHtmlTemplate, // Save as HTML template
        positiveWindowDesignContent: contentToSave, // Save the design content object
      };
    }
    
    // Save negative window content if negative editor is active
    if (inlineEditingType === 'negative') {
      // Generate HTML template for negative window design
      const negativeWindowHtmlTemplate = `<!DOCTYPE html>
<html>
<head>
<style>
body {
  font-family: Arial, sans-serif;
  font-size: 16px;
  margin: 0;
  padding: 20px;
  background-color: #f4f4f4;
  color: #000000;
}
.container {
  background-color: #fff;
  padding: 20px;
  border-radius: 5px;
  box-shadow: 0 2px 5px rgba(0,0,0,.1);
}
.negative-header {
  text-align: center;
  margin-bottom: 20px;
}
.negative-heading {
  font-size: 24px;
  font-weight: bold;
  color: #dc2626;
  margin-bottom: 10px;
}
.negative-description {
  font-size: 16px;
  color: #374151;
  line-height: 1.5;
}
.image-container {
  text-align: center;
  margin: 20px 0;
}
.image-container img {
  max-width: 200px;
  height: auto;
  border-radius: 8px;
}
</style>
</head>
<body>
  <div class="container">
    <div class="negative-header">
      <h1 class="negative-heading">${negativeWindowContent.heading}</h1>
      <p class="negative-description">${negativeWindowContent.description}</p>
    </div>
    
    <div class="image-container">
      <img src="${negativeWindowContent.imageUrl}" alt="We apologize" />
    </div>
  </div>
</body>
</html>`;
      
      // Create content object - preserve user-uploaded images (data URLs)
      const contentToSave = {
        ...negativeWindowContent,
      };
      // Only convert default paths to empty string, preserve all data URLs and other URLs
      if (contentToSave.imageUrl) {
        // If it's a data URL (user uploaded), keep it as-is
        if (!contentToSave.imageUrl.startsWith('data:')) {
          // If it's the default hardcoded path, convert to empty string
          if (contentToSave.imageUrl === '/src/assets/illustrations/bad-review.svg') {
            contentToSave.imageUrl = '';
          }
          // Otherwise, keep it as-is (could be imported URL or other custom URL)
        }
      }
      
      updatedData = {
        ...updatedData,
        updatedNegativeWindowTemplate: negativeWindowHtmlTemplate, // Save as HTML template
        negativeWindowDesignContent: contentToSave, // Save the design content object
      };
    }
    
    localStorage.setItem(storageProperty, JSON.stringify(updatedData));
    
    // Close the template list
    onClose();
  };

  // Get saved data from localStorage
  const getSavedData = () => {
    try {
      const storedData = localStorage.getItem(storageProperty);
      return storedData ? JSON.parse(storedData) : {};
    } catch {
      return {};
    }
  };

  const getPreviewHtml = (template: Template) => {
    const savedData = getSavedData();
    
    if (template.type === 'email') {
      // Use current state when editor is open, otherwise use saved data
      const emailData = inlineEditingType === 'email' ? emailInlineContent : (savedData.emailDesignContent || emailInlineContent);
      return getEmailPreviewHtml(emailData);
    }
    if (template.type === 'message') {
      const businessName = (() => {
        try {
          const raw = localStorage.getItem(storageProperty);
          const parsed = raw ? JSON.parse(raw) : {};
          return parsed.businessName || 'Business';
        } catch {
          return 'Business';
        }
      })();
      
      // Use current state when editor is open, otherwise use saved data
      const messageData = inlineEditingType === 'message' ? {
        logoUrl: messageLogo,
        previewName: messagePreviewName,
        message: messageInlineContent
      } : (savedData.messageDesignContent || {
        logoUrl: messageLogo,
        previewName: messagePreviewName,
        message: messageInlineContent
      });
      
      return getMessagePreviewHtml(messageData, businessName);
    }
    if (template.type === 'review') {
      const businessName = (() => {
        try {
          const raw = localStorage.getItem(storageProperty);
          const parsed = raw ? JSON.parse(raw) : {};
          return parsed.businessName || 'Business';
        } catch {
          return 'Business';
        }
      })();
      
      // Get saved review form content from localStorage
      const savedData = getSavedData();
      const reviewData = inlineEditingType === 'review' ? reviewFormContent : (savedData.reviewFormDesignContent || reviewFormContent);
      
      return getReviewPreviewHtml(reviewData, businessName, businessLogo);
    }
    if (template.type === 'feedback') {
      const businessName = (() => {
        try {
          const raw = localStorage.getItem(storageProperty);
          const parsed = raw ? JSON.parse(raw) : {};
          return parsed.businessName || 'Business';
        } catch {
          return 'Business';
        }
      })();
      
      // Get saved feedback form content from localStorage
      const savedData = getSavedData();
      const feedbackData = inlineEditingType === 'feedback' ? feedbackFormContent : (savedData.feedbackFormDesignContent || feedbackFormContent);
      
      return getFeedbackPreviewHtml(feedbackData, businessName, businessLogo);
    }
    if (template.type === 'positive') {
      const businessName = (() => {
        try {
          const raw = localStorage.getItem(storageProperty);
          const parsed = raw ? JSON.parse(raw) : {};
          return parsed.businessName || 'Business';
        } catch {
          return 'Business';
        }
      })();
      
      // Get saved positive window content from localStorage
      const savedData = getSavedData();
      const positiveData = inlineEditingType === 'positive' ? positiveWindowContent : (savedData.positiveWindowDesignContent || positiveWindowContent);
      
      return getPositivePreviewHtml(positiveData, businessName, businessLogo);
    }
    if (template.type === 'negative') {
      const businessName = (() => {
        try {
          const raw = localStorage.getItem(storageProperty);
          const parsed = raw ? JSON.parse(raw) : {};
          return parsed.businessName || 'Business';
        } catch {
          return 'Business';
        }
      })();
      
      // Get saved negative window content from localStorage
      const savedData = getSavedData();
      const negativeData = inlineEditingType === 'negative' ? negativeWindowContent : (savedData.negativeWindowDesignContent || negativeWindowContent);
      
      return getNegativePreviewHtml(negativeData, businessName, businessLogo);
    }
    return template.template;
  };

  if (!open) return null;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
        <div className="flex items-start gap-3">
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {inlineEditingType 
                ? (() => {
                    switch (inlineEditingType) {
                      case 'email':
                        return 'Email design';
                      case 'message':
                        return 'Message design';
                      case 'review':
                        return 'Review form design';
                      case 'feedback':
                        return 'Feedback form design';
                      case 'positive':
                        return 'Positive window design';
                      case 'negative':
                        return 'Negative window design';
                      default:
                        return templateMode === 'text' ? 'Default text template' : 'Default email template';
                    }
                  })()
                : templateMode === 'text' ? 'Default text template' : 'Default email template'
              }
            </h2>
            <p className="text-sm mt-2" style={{ color: '#8B919F' }}>
              {inlineEditingType 
                ? (() => {
                    switch (inlineEditingType) {
                      case 'email':
                        return 'Customize the design of the main email that will be sent to the customer with a request for review';
                      case 'message':
                        return 'Customize the design of the main message that will be sent to the customer with a request for review';
                      case 'review':
                        return 'Customize the design of the window for the customer to leave a public review.';
                      case 'feedback':
                        return 'Customize the design of the window for leaving private customer feedback for your business.';
                      case 'positive':
                        return 'Customize the design of the window for the customer to see after leaving a positive review.';
                      case 'negative':
                        return 'Customize the design of the window for the customer to see after leaving a negative review.';
                      default:
                        return templateMode === 'text' ? 'Customize the design of the main message that will be sent to the customer with a request for review' : 'Customize the design of the main email that will be sent to the customer with a request for review';
                    }
                  })()
                : templateMode === 'text' ? 'Customize the design of the main message that will be sent to the customer with a request for review' : 'Customize the design of the main email that will be sent to the customer with a request for review'
              }
            </p>
          </div>
        </div>
        <button
          onClick={handleSaveAndContinue}
          className="px-4 py-2 bg-[#52B6FE] text-white rounded-lg hover:bg-base-200 transition-colors font-medium"
        >
          Save & continue
        </button>
      </div>

      <div className="flex flex-1 overflow-hidden bg-gray-50 p-6">
        {/* Left Sidebar - Template List or Inline Editor */}
        <div className={clsx("w-2/5 bg-white rounded-lg shadow-sm border border-gray-200 overflow-y-auto mr-4", inlineEditingType ? "p-6" : "p-6")}>
          {inlineEditingType ? (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-gray-900">Design settings</h3>
              </div>
              
              {/* Divider Line */}
              <div className="border-b border-gray-200 mb-6 mt-4"></div>
              
              {inlineEditingType === 'message' && (
                <MessageInlineEditor 
                  content={{
                    logoUrl: messageLogo || '',
                    previewName: messagePreviewName,
                    message: messageInlineContent
                  }}
                  onChange={(content) => {
                    setMessageLogo(content.logoUrl);
                    setMessagePreviewName(content.previewName);
                    setMessageInlineContent(content.message);
                  }}
                />
              )}
              
              {inlineEditingType === 'email' && (
                <EmailInlineEditor 
                  content={emailInlineContent}
                  onChange={setEmailInlineContent}
                />
              )}

              {inlineEditingType === 'review' && (
                <ReviewFormInlineEditor 
                  content={reviewFormContent}
                  onChange={setReviewFormContent}
                />
              )}

              {inlineEditingType === 'feedback' && (
                <FeedbackFormDesignEditor 
                  content={feedbackFormContent}
                  onChange={setFeedbackFormContent}
                />
              )}

              {inlineEditingType === 'positive' && (
                <PositiveWindowDesignEditor 
                  content={positiveWindowContent}
                  onChange={setPositiveWindowContent}
                />
              )}

              {inlineEditingType === 'negative' && (
                <NegativeWindowDesignEditor 
                  content={negativeWindowContent}
                  onChange={setNegativeWindowContent}
                />
              )}
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Template information</h2>
                <hr className="my-4" />
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Template name
                    </label>
                    <input
                      type="text"
                      value={selectedTemplate.name}
                      disabled
                      className="w-full p-2 rounded-lg text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                      placeholder="Default email template"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Template design</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Change the content of the screens for this {templateMode === 'text' ? 'message' : 'email'}
                </p>
                <hr className="my-4" />
                <div className="space-y-3">
                  {templates.map((template) => (
                    <div
                      key={template.id}
                      className="p-4 rounded-lg bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => handleTemplateSelect(template)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 capitalize flex items-center gap-2">
                            {template.type === 'review' ? 'Review form design' : 
                             template.type === 'feedback' ? (
                               <>
                                 Feedback form design
                                 <Tooltip 
                                   content={
                                     <div className="text-sm">
                                       <div>If a review is less than 2 stars, your customer can</div>
                                       <div>mark it as private. You'll receive this private feedback</div>
                                       <div>on the &quot;Feedback&quot; page to address and resolve</div>
                                       <div>the customer's concerns.</div>
                                     </div>
                                   }
                                   direction="up"
                                 >
                                   <InfoIcon className="w-4 h-4 text-gray-500 cursor-help" />
                                 </Tooltip>
                               </>
                             ) : 
                             template.type === 'positive' ? 'Positive window design' :
                             template.type === 'negative' ? 'Negative window design' :
                             `${template.type} design`}
                          </h4>
                          <p className="text-sm text-gray-600 mt-1">
                            {template.type === 'email' ? 'Customize how the message that will be sent to the customer(s) will look like' :
                             template.type === 'message' ? 'Customize how the message that will be sent to the customer(s) will look like' :
                             template.type === 'review' ? 'Customize how the feedback window will look like for the customer(s)' :
                             template.type === 'feedback' ? 'Customize how the feedback window will look like for the customer(s)' :
                             template.type === 'positive' ? 'Customize how the window after a positive review will look for the customer(s)' :
                             template.type === 'negative' ? 'Customize how the window after a negative review will look for the customer(s)' :
                             template.description}
                          </p>
                        </div>
                        <button
                          className="flex items-center gap-2 px-3 py-2 bg-[#E7EAF4] border border-gray-300 rounded-lg text-gray-700 text-sm font-medium mt-4"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditTemplate(template);
                          }}
                        >
                          <EditIcon className="w-4 h-4" />
                          Edit
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Right Sidebar - Template Preview */}
        <div className="flex-1 flex flex-col bg-white overflow-hidden" style={{ border: 'none' }}>
          {/* Preview Header - Only show when editing */}
          {inlineEditingType && (
            <div className="bg-white px-4 py-3" style={{ border: 'none', borderBottom: 'none' }}>
              <h3 className="text-sm font-bold text-gray-900">Preview</h3>
              <hr className="mt-7 border-gray-200" />
            </div>
          )}

          {/* Tab Navigation */}
          {!inlineEditingType && (
            <div className="bg-[#CED3E2] rounded-full p-1 mx-4 mt-4">
            <div className="flex">
              {templateTypes.map((type) => (
                <button
                  key={type.key}
                  className={clsx(
                    'px-6 py-2 text-sm font-medium rounded-full transition-colors flex-1',
                    activeTab === type.key
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  )}
                  onClick={() => {
                    setActiveTab(type.key);
                    const matchedTemplate = templates.find(
                      (t) => t.type === (type.key as Template['type'])
                    );
                    if (matchedTemplate) {
                      setSelectedTemplate(matchedTemplate);
                    }
                  }}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>
          )}

          {/* Preview Content */}
          <div className={clsx("flex-1 bg-white", inlineEditingType ? "p-6 pt-4" : "p-6")} style={{ border: 'none' }}>
            <div className="bg-gray-50 overflow-hidden h-full p-6" style={{ border: 'none' }}>
              <div className="bg-white h-full overflow-hidden" style={{ border: 'none' }}>
                <iframe
                  srcDoc={getPreviewHtml(selectedTemplate)}
                  title="Template Preview"
                  className="w-full h-full border-0"
                  style={{ height: '100%' }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {editingTemplate && (
        <PageTemplateEditor
          open={isTemplateEditorOpen}
          onClose={() => {
            setIsTemplateEditorOpen(false);
            setEditingTemplate(null);
          }}
          onSave={handleTemplateSave}
          initialTemplate={editingTemplate.template}
          templateType={editingTemplate.type}
        />
      )}

    </div>
  );
};
